import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { brandCopy, type Banner } from '@skytech/shared';

import { useBanners } from './bannersApi';
import { uploadUrl } from '../../lib/format';
import { whatsappLink } from '../../lib/whatsapp';

/**
 * Homepage hero (SRS §4.2) — a carousel of admin-managed banners. Each banner drives one
 * slide: optional campaign photography (→ bundled product artwork), eyebrow/headline/subtitle and up to
 * two CTAs. When no banners are active it shows the branded default slide, so the hero is
 * never empty. Swipeable, zero-CLS; mobile auto-advances after reading time while
 * desktop keeps discreet controls over the image edge (SRS §4.2, §8.1).
 */
type Slide = Pick<
  Banner,
  | 'eyebrow'
  | 'title'
  | 'subtitle'
  | 'imagePath'
  | 'ctaLabel'
  | 'linkUrl'
  | 'secondaryCtaLabel'
  | 'secondaryCtaUrl'
>;

/** Brand fallback used when no campaign is active; artwork is illustrative, not a SKU. */
const DEFAULT_SLIDE: Slide = {
  eyebrow: brandCopy.eyebrow,
  title: brandCopy.tagline,
  subtitle:
    'Find CCTV cameras, Wi-Fi routers and everyday electronics for your home or business. Compare products and choose delivery or store pickup.',
  imagePath: null,
  ctaLabel: 'Explore products',
  linkUrl: '/products',
  secondaryCtaLabel: 'Get a quote',
  secondaryCtaUrl: whatsappLink('Hello SkyTech Solutions, I would like a quote.') ?? '/products',
};

export function HeroCarousel(): JSX.Element {
  const { data: banners = [] } = useBanners();
  const slides: Slide[] = banners.length > 0 ? banners : [DEFAULT_SLIDE];
  const n = slides.length;

  const [index, setIndex] = useState(0);
  const [warmNext, setWarmNext] = useState(false);
  const [canRotate, setCanRotate] = useState(false);

  useEffect(() => {
    setIndex((i) => (i >= n ? 0 : i));
  }, [n]);

  useEffect(() => {
    if (n < 2) return;
    const timer = window.setTimeout(() => setWarmNext(true), 1500);
    return () => window.clearTimeout(timer);
  }, [n]);

  useEffect(() => {
    const mobile = window.matchMedia('(max-width: 767px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setCanRotate(mobile.matches && !reduced.matches && !document.hidden);
    update();
    mobile.addEventListener('change', update);
    reduced.addEventListener('change', update);
    document.addEventListener('visibilitychange', update);
    return () => {
      mobile.removeEventListener('change', update);
      reduced.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  useEffect(() => {
    if (n < 2 || !canRotate) return;
    const timer = window.setTimeout(() => setIndex((i) => (i + 1) % n), 8000);
    return () => window.clearTimeout(timer);
  }, [index, n, canRotate]);

  const startX = useRef<number | null>(null);
  function onTouchStart(e: React.TouchEvent) {
    startX.current = e.touches[0]?.clientX ?? null;
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (startX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - startX.current;
    if (Math.abs(dx) > 50) setIndex((i) => (dx < 0 ? (i + 1) % n : (i - 1 + n) % n));
    startX.current = null;
  }

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Promotions"
      className="relative mx-auto max-w-7xl px-4 pt-4 text-white md:px-6 md:pt-6"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <h1 className="sr-only">CCTV, networking and electronics in Kenya</h1>
      <div className="relative overflow-hidden rounded-2xl bg-navy-900">
        {/* §7.6/§8.1: fixed track geometry prevents late CMS copy and hidden slides shifting products. */}
        <div
          className="flex h-[28rem] transition-transform duration-500 ease-in-out motion-reduce:transition-none md:h-[23rem]"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((s, i) => (
            <HeroSlide
              key={i}
              slide={s}
              priority={i === 0}
              active={i === index}
              preload={warmNext && i === (index + 1) % n}
            />
          ))}
        </div>
        {n > 1 && (
          <div
            className="absolute bottom-2 right-3 z-10 hidden md:flex"
            role="group"
            aria-label="Choose slide"
          >
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-pressed={i === index}
                aria-label={`Show slide ${i + 1} of ${n}`}
                onClick={() => setIndex(i)}
                className="flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white"
              >
                <span
                  aria-hidden
                  className={`h-2 rounded-full drop-shadow-md transition-all duration-300 ${i === index ? 'w-5 bg-white' : 'w-2 bg-white/70 hover:bg-white'}`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HeroSlide({
  slide,
  priority,
  active,
  preload,
}: {
  slide: Slide;
  priority: boolean;
  active: boolean;
  preload: boolean;
}): JSX.Element {
  const campaign = slide.imagePath ? uploadUrl(slide.imagePath) : null;
  return (
    <div
      className="grid w-full shrink-0 grid-rows-[minmax(0,1fr)_10rem] md:grid-cols-[1.05fr_1fr] md:grid-rows-1"
      aria-hidden={!active}
      {...(!active ? { inert: '' } : {})}
    >
      <div className="flex min-h-0 min-w-0 flex-col justify-center p-5 sm:p-8 lg:p-10">
        {slide.eyebrow && (
          <p className="hidden text-[11px] uppercase tracking-[0.18em] text-white/75 md:block">
            {slide.eyebrow}
          </p>
        )}
        <h2 className="mt-3 line-clamp-2 max-w-xl text-[1.75rem] font-light leading-tight tracking-wide sm:text-3xl lg:text-[2.75rem]">
          {slide.title}
        </h2>
        {slide.subtitle && (
          <p className="mt-3 line-clamp-3 max-w-lg text-sm leading-relaxed text-white/80">
            {slide.subtitle}
          </p>
        )}
        {(slide.ctaLabel || slide.secondaryCtaLabel) && (
          <div className="mt-5 flex flex-wrap gap-3">
            <Cta label={slide.ctaLabel} url={slide.linkUrl} variant="primary" />
            <Cta label={slide.secondaryCtaLabel} url={slide.secondaryCtaUrl} variant="secondary" />
          </div>
        )}
      </div>
      <div className="relative min-h-0 overflow-hidden bg-cloud">
        {(priority || active || preload) && (
          <picture>
            {/* §8.1: responsive bundled fallback; campaign uploads remain admin-controlled.
              Image is adjacent to copy, so photography needs no contrast-dimming overlay. */}
            {!campaign && (
              <source
                srcSet="/images/security-networking-hero-640.webp 640w, /images/security-networking-hero-960.webp 960w"
                sizes="(min-width:1280px) 600px, (min-width:768px) 48vw, 100vw"
                type="image/webp"
              />
            )}
            <img
              src={campaign ?? '/images/security-networking-hero-640.webp'}
              width={960}
              height={640}
              alt=""
              aria-hidden
              loading="eager"
              {...(priority ? { fetchpriority: 'high' } : {})}
              className={`absolute inset-0 h-full w-full ${campaign ? 'object-cover object-right' : 'object-cover'}`}
            />
          </picture>
        )}
      </div>
    </div>
  );
}

/** Renders a CTA only when both label + url are set; internal paths use the router,
 * external URLs open in a new tab with a safe rel. */
function Cta({
  label,
  url,
  variant,
}: {
  label: string | null;
  url: string | null;
  variant: 'primary' | 'secondary';
}): JSX.Element | null {
  if (!label || !url) return null;
  const cls =
    variant === 'primary'
      ? 'inline-flex min-h-11 items-center justify-center rounded-lg bg-sky-600 px-3 py-3 text-xs sm:px-5 sm:text-sm font-medium text-white transition-shadow hover:shadow-hover'
      : 'inline-flex min-h-11 items-center justify-center rounded-lg border border-white/40 px-3 py-3 text-xs sm:px-5 sm:text-sm font-medium text-white transition-colors hover:border-white';
  return url.startsWith('/') ? (
    <Link to={url} className={cls}>
      {label}
    </Link>
  ) : (
    <a href={url} target="_blank" rel="noopener noreferrer" className={cls}>
      {label}
    </a>
  );
}
